import cn from 'classnames'
import {
  type ComponentProps,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { useSafetyTranslation } from 'cng-common/features/translations'
import {
  PLAN_SECTION_DECK_ATTRIBUTE,
  PLAN_SECTION_DECK_ROOM_VAR,
  usePlanSectionDeckSlot,
} from 'partner360/lib/planSectionDialog'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { CockpitIconButton } from '../../../components/cockpitIconButton'
import { useCockpitScope } from '../../hooks/useCockpitScope'
import { CockpitPlanDeck, useDeckPeek } from '../cockpitPlanDeck'

import styles from './CockpitSectionDeck.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.planDeck

/** Air between the strip and the dialog under it. */
const STRIP_GAP = 12
/** A pinned deck stands this far from the top of the screen and from the dialog under it. */
const PINNED_INSET = 16
/* Over a full-screen section a pointer slips off the deck easily, on its way to a menu or past an
   edge: the deck waits this long before going back up, and stays if the pointer comes back. */
const CLOSE_DELAY_MS = 500

interface ICockpitSectionDeckProps {
  /** The deck as the tray draws it; drawn again, unfolded, over the open section. */
  deck: ComponentProps<typeof CockpitPlanDeck>
  /** The offering tabs the strip at the top of the screen carries. */
  tabs: ReactNode
}

/**
 * The plan deck over an open plan section, which covers the tray with its scrim. At rest only a
 * strip hangs from the top of the screen: the year, the offering tabs and a button at its end. A
 * tab switches the offering in place. The button drops the whole deck over the section, on a click
 * or with the pointer resting on it, as the folded deck unfolds over the board; only the button
 * does, so the pointer crossing the strip to a tab never covers the tabs. The deck goes back up
 * once the pointer leaves it, on a click elsewhere or on Escape.
 *
 * The chevron on the dropped deck sends it back up. The pin keeps it down: the deck then stands above the scrim at the top of the screen and the
 * section dialog starts below it, giving up the height. Unpinned, the dialog takes it back.
 */
export const CockpitSectionDeck = ({ deck, tabs }: ICockpitSectionDeckProps) => {
  const t = useSafetyTranslation()
  const scope = useCockpitScope()
  const slot = usePlanSectionDeckSlot()
  const [isPinned, setIsPinned] = useState(false)
  /* A closed section takes its slot along; the next one opens with the deck up. */
  const peek = useDeckPeek(Boolean(slot) && !isPinned, { closeDelayMs: CLOSE_DELAY_MS })
  const { close } = peek
  const stripRef = useRef<HTMLDivElement>(null)
  const dropRef = useRef<HTMLDivElement>(null)
  /* Set while a press lands in the deck or in a list or panel opened from it, which React carries
     through its portal: the document hears the press after the deck does. */
  const isPressInside = useRef(false)
  const isDown = peek.isOpen || isPinned

  /* The room the dialog keeps clear at the top: the strip's, or the pinned deck's with its air. */
  useLayoutEffect(() => {
    const element = isPinned ? dropRef.current : stripRef.current

    if (!slot || !element) {
      return
    }

    const rootStyle = document.documentElement.style
    const updateRoom = () =>
      rootStyle.setProperty(
        PLAN_SECTION_DECK_ROOM_VAR,
        `${isPinned ? element.offsetHeight + PINNED_INSET * 2 : element.offsetHeight + STRIP_GAP}px`
      )
    const observer = new ResizeObserver(updateRoom)

    updateRoom()
    observer.observe(element)

    return () => {
      observer.disconnect()
      rootStyle.removeProperty(PLAN_SECTION_DECK_ROOM_VAR)
    }
  }, [isPinned, slot])

  useEffect(() => {
    if (!peek.isOpen) {
      return
    }

    const handlePress = () => {
      if (!isPressInside.current) {
        close()
      }

      isPressInside.current = false
    }

    document.addEventListener('pointerdown', handlePress)

    return () => document.removeEventListener('pointerdown', handlePress)
  }, [close, peek.isOpen])

  if (!slot) {
    return null
  }

  /* Sends the deck back up to the strip, a pinned one included. */
  const hide = (
    <CockpitIconButton
      icon="kbase kbase-chevron-top"
      label={t(KEYS.hide)}
      onClick={() => {
        setIsPinned(false)
        close()
      }}
    />
  )

  const pin = (
    <CockpitIconButton
      icon="kbase kbase-pushpin"
      label={t(isPinned ? KEYS.unpinOverSection : KEYS.pinOverSection)}
      pressed={isPinned}
      onClick={() => {
        setIsPinned((value) => !value)
        close()
      }}
    />
  )

  return createPortal(
    <div
      className={cn(styles.root, { [styles.open]: peek.isOpen, [styles.pinned]: isPinned })}
      {...{ [PLAN_SECTION_DECK_ATTRIBUTE]: '' }}
      onPointerDownCapture={() => {
        isPressInside.current = true
      }}
      /* Coming back over the dropped deck keeps it down; only the button drops it. */
      onMouseEnter={() => peek.isOpen && peek.handlers.onMouseEnter()}
      onMouseLeave={peek.handlers.onMouseLeave}
      onBlur={peek.handlers.onBlur}
      onKeyDown={peek.handlers.onKeyDown}
    >
      {/* Under the dropped deck it stays where it is, so the pointer resting on it keeps the deck
          down; a pinned deck takes its place. */}
      <div
        ref={stripRef}
        className={styles.strip}
        inert={isPinned || undefined}
        aria-hidden={isDown || undefined}
      >
        <span className={styles.year}>{scope.fYearLabel}</span>
        <span className={styles.rule} aria-hidden="true" />
        <div className={styles.tabs}>{tabs}</div>
        {/* Leaving it before the deck drops calls the drop off. */}
        <span
          className={styles.show}
          onMouseEnter={peek.handlers.onMouseEnter}
          onMouseLeave={() => !peek.isOpen && close()}
          onFocus={peek.handlers.onFocus}
        >
          <CockpitIconButton
            icon="kbase kbase-chevron-bottom"
            label={t(KEYS.show)}
            expanded={peek.isOpen}
            onClick={peek.open}
          />
        </span>
      </div>

      <div
        ref={dropRef}
        className={styles.drop}
        inert={!isDown || undefined}
        aria-hidden={!isDown || undefined}
      >
        <CockpitPlanDeck
          {...deck}
          tools={
            <>
              {hide}
              {pin}
              {deck.tools}
            </>
          }
        />
      </div>
    </div>,
    slot
  )
}

CockpitSectionDeck.displayName = 'CockpitSectionDeck'
